import React, { useContext, useEffect, useState } from 'react';

import { Button } from '@jobber/components/Button';
import { useDispatch, useSelector } from 'react-redux';
import io from 'socket.io-client';

import { showToast } from '@jobber/components/Toast';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { useLocation } from 'react-router';
import jobberLogo from '../../../assets/logo.svg';

import QuoteDateRangeModal from '../../../components/Common/QuoteDateRangeModal';
import { postToServer } from '../../../components/Common/requests';
import { SocketContext } from '../../../SocketProvider';
import { setStartSyncR } from '../../../store/layoutSlice';
import { setLoginUser } from '../../../store/userSlice';
import { useQuoteFilter } from '../FilterQuery/QuoteFilterQuery';
import { useJobberSync } from '../../../hooks/use-jobber-sync';

function LoadData({ handleSynQuotes }) {
	const dispatch = useDispatch();
	const { socket, socketHandler } = useContext(SocketContext);
	const [currentStatus, setCurrentStatus] = useState('');
	const [stopPrgress, setStopPrgress] = useState(null);
	const [progressValue, setProgressValue] = useState(1);
	const [dateModal, setDateModal] = useState(false);

	const userStore = useSelector((store) => store.user);
	const user = userStore?.user?.data;
	const { job: durableSyncJob, isActive: hasActiveJob, progress: durableProgress, trackJob } = useJobberSync('quotes');

	useEffect(() => {
		if (hasActiveJob) {
			setLoading(true);
			setProgressValue(durableProgress);
			setCurrentStatus(durableSyncJob?.stage || 'Syncing quotes');
		} else if (durableSyncJob?.status === 'completed') {
			setLoading(false);
			setProgressValue(100);
			setCurrentStatus('');
			handleSynQuotes();
			dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: false, progress: '0' } }));
		} else if (durableSyncJob && ['failed', 'cancelled'].includes(durableSyncJob.status)) {
			setLoading(false);
			setCurrentStatus(durableSyncJob.stage || '');
			dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: false, progress: '0' } }));
		}
	}, [durableSyncJob?.id, durableSyncJob?.status, durableSyncJob?.stage, durableProgress, hasActiveJob]);

	// for header button sync
	const location = useLocation();
	useEffect(() => {
		if (location?.state?.sync) {
			setDateModal(true)
			window.history.replaceState({}, '')
		}
	}, [location]);
	// for header button sync  end

	useEffect(() => {
		if (stopPrgress) {
			showToast({
				message: stopPrgress?.message,
				variation: 'error'
			});
			handleSynQuotes();
			setLoading(false);
		}
	}, [stopPrgress]);

	useEffect(() => {
		if (!socket) {
			const newSocket = io(process.env.REACT_APP_API_URL);
			socketHandler(newSocket);
		}

		if (socket) {
			socket.on(`Genie_Quote_progress_${user?._id}`, (notify) => {
				setLoading(true);
				setProgressValue(notify?.progress);
				setCurrentStatus(notify?.message);
				dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: true, progress: notify?.progress } }));
			});

			socket.on(`Genie_Quote_syncCompleted_${user?._id}`, (notify) => {
				showToast({
					message: 'Fetch Completed'
				});
				setCurrentStatus('');
				setProgressValue(notify?.progress);
				handleSynQuotes();
				setLoading(false);
				dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: false, progress: '0' } }));
			});

			socket.on(`Genie_Quote_syncStarted_${user?._id}`, (notify) => {
				setDateModal(false);
				setLoading(true);
				setProgressValue(notify?.progress);
				setCurrentStatus(notify?.message);
				showToast({
					message: 'Please wait while we are syncing.',
					variation: 'info'
				});
				dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: true, progress: notify.progress } }));
			});

			socket.on(`Genie_error_${user?._id}`, (notify) => {
				setStopPrgress(notify);
			});

			socket.on('error', (errorMessage) => {
				console.error(errorMessage);
			});

			socket.on('connect', (successMessage) => {
				console.log('socket connected');
			});

			socket.on(`QuickBooksSyncStart_${user?._id}`, (msg) => {
				setLoading(true);
				setProgressValue(msg.progress);
				setCurrentStatus(msg.message);
				showToast({
					message: msg.message,
					variation: 'info'
				});
				dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: true, progress: msg.progress } }));
			});

			socket.on(`QuickBooksSyncProgress_${user?._id}`, (msg) => {
				setLoading(true);
				setCurrentStatus(msg.message);
				setProgressValue(msg.progress);
				dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: true, progress: msg.progress } }));
			});

			socket.on(`QuickBooksSyncComplete_${user?._id}`, (msg) => {
				setLoading(false);
				setProgressValue(msg.progress);
				setCurrentStatus(msg.message);
				showToast({
					message: msg.message,
					variation: 'info'
				});
				dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: false, progress: msg.progress } }));
			});

			socket.on(`QuickBooksSyncError_${user?._id}`, (msg) => {
				setLoading(false);
				setCurrentStatus(msg.message);
				setProgressValue(msg.progress);
				showToast({
					message: msg.message,
					variation: 'info'
				});
				dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: false, progress: msg.progress } }));
			});
		}
	}, [socket]);

	const joinRoom = async () => {
		if (socket && user?._id) {
			await socket.emit('joinGenieRoom', { userId: user?._id });
		}
	};

	useEffect(() => {
		joinRoom();
	}, [user?._id, socket]);

	const [loading, setLoading] = useState(false);
	useEffect(() => {
		if (user?.isQuoteSyncing) {
			setLoading(true);
			setProgressValue(user?.progress);
		} else {
			setLoading(false);
			setProgressValue(1);
		}
	}, [user]);
	const syncAllClientHandler = async (s, e, statuses) => {
		setDateModal(false);
		setLoading(true);
		dispatch(setStartSyncR(true))
		showToast({
			message: 'Please wait while we are syncing.',
			variation: 'info'
		});
		const result = await postToServer('sync-quotes', { startDate: s, endDate: e, statuses });
		if (result.status) {
			trackJob(result.syncJob);
			dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: true, progress: '1' } }));
		} else {
			showToast({
				message: result.message || result.error,
				variation: 'error'
			});
			dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: false, progress: '1' } }));
			setLoading(false);
		}
	};

	async function handleSyncQB() {
		setLoading(true);
		showToast({
			message: 'Please wait while we are syncing.',
			variation: 'info'
		});
		const response = await postToServer('qb/sync-clients')
		if (response.status) {
			dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: true, progress: '1' } }));
		} else {
			showToast({
				message: response.message || response.error,
				variation: 'error'
			});
			dispatch(setLoginUser({ ...userStore?.user, data: { ...userStore?.user?.data, isQuoteSyncing: false, progress: '1' } }));
			setLoading(false);
		}
	}

	return (
		<>
			<QuoteDateRangeModal open={dateModal} onConfirm={syncAllClientHandler} onCancel={() => setDateModal(false)} />
			{loading ? (
				<div className="mb-2">
					<p className="text-center">{currentStatus}</p>
					<ProgressBar animated variant="success" label={`${progressValue}%`} now={progressValue} />
					<p style={{ color: 'red', fontSize: '12px', textAlign: 'center' }} className="mt-1">
						Do not perform any operation, while we are syncing
					</p>
				</div>
			) : (
				<>
					<Button label={<>Sync <img src={jobberLogo} width={20} alt="" /></>} onClick={() => setDateModal(true)} />
					<Button icon='quickbooks' iconOnRight label={'Sync'} onClick={handleSyncQB} />
				</>
			)}
		</>
	);
}

export default LoadData;
